"use client";

import { useQuery } from "@tanstack/react-query";
import {
  getPastEventsForHiive,
  getUpcomingEventsForHiive,
} from "@/actions/event";
import { getHiiveMembersWithProfiles } from "@/actions/membership";
import { getPostsForHiive } from "@/actions/post";
import {
  type HiiveMemberListItem,
  mapHiiveMemberProfiles,
} from "@/features/hiives/components/hiive-detail-hub/hiive-page/hiive-members.shared";

type UpcomingEvents = Extract<
  Awaited<ReturnType<typeof getUpcomingEventsForHiive>>,
  { ok: true }
>["data"];
type PastEvents = Extract<
  Awaited<ReturnType<typeof getPastEventsForHiive>>,
  { ok: true }
>["data"];

export type HiiveEventsData = {
  upcoming: UpcomingEvents;
  past: PastEvents;
};

// Hook to get upcoming and past events for a hiive.
// `initialData` is server-fetched and passed in so the events tab renders
// instantly without a client-side server-action call on mount (see
// use-posts.ts / hiive-detail-section for the rationale around client-side
// navigation in dev).
export function useHiiveEvents(hiiveId: number, initialData?: HiiveEventsData) {
  return useQuery({
    queryKey: ["hiive", hiiveId, "events"],
    queryFn: async () => {
      const [upcomingResult, pastResult] = await Promise.all([
        getUpcomingEventsForHiive(hiiveId),
        getPastEventsForHiive(hiiveId),
      ]);

      return {
        upcoming: upcomingResult.ok ? upcomingResult.data : [],
        past: pastResult.ok ? pastResult.data : [],
      } satisfies HiiveEventsData;
    },
    initialData,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: false,
    enabled: !!hiiveId,
  });
}

// Hook to get hiive members
export function useHiiveMembers(
  hiiveId: number,
  initialData?: HiiveMemberListItem[],
) {
  return useQuery({
    queryKey: ["hiive", hiiveId, "members"],
    queryFn: async () => {
      const result = await getHiiveMembersWithProfiles(hiiveId);
      if (!result.ok) return [];

      return mapHiiveMemberProfiles(result.data || []);
    },
    initialData,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: false,
    enabled: !!hiiveId,
  });
}

// Hook to get hiive posts
export function useHiivePosts(hiiveId: number, userId?: string) {
  return useQuery({
    queryKey: ["hiive", hiiveId, "posts", userId ?? "guest"],
    queryFn: async () => {
      const result = await getPostsForHiive(hiiveId, userId);
      return result.ok ? result.data || [] : [];
    },
    staleTime: 1 * 60 * 1000, // 1 minute - posts change more frequently
    refetchOnMount: false,
    enabled: !!hiiveId,
  });
}
